import { createStore } from 'vuex'
import services from './services.json'
import valoresEquipo from './valores-equipo.json'
import fasesDesarrollo from './fases-desarrollo.json'
import { sortEstudiosByDateDesc } from '@/utils/sortEstudios'
import { sortCasosByFechaInicioDesc } from '@/utils/sortCasos'
import { findCasoBySlug } from '@/utils/casoSlug'
import { findPrensaBySlug } from '@/utils/prensaSlug'
import { sortPrensaByDateDesc } from '@/utils/sortPrensa'
import { sortPodcastsByDateDesc } from '@/utils/sortPodcasts'
import { normalizeApiCollectionDates, formatDateToDdMmYyyy, getYearFromDate } from '@/utils/formatDate'
let heroSlidesLoadPromise = null
let estudiosLoadPromise = null
let casosLoadPromise = null
let equipoLoadPromise = null
let prensaLoadPromise = null
let eventosLoadPromise = null
let podcastsLoadPromise = null

export const SERVICE_CATEGORY_ID = Object.freeze({
  estrategiaValorCorporativo: '01',
  gestionGruposInteres: '02',
  dataAnalytics: '03',
  tecnologiaDesarrolloSoftware: '04',
})

export default createStore({
  state: {
    services: services,
    estudios: [],
    estudiosLoaded: false,
    prensa: [],
    prensaLoaded: false,
    casos: [],
    podcasts: [],
    podcastsLoaded: false,
    eventos: [],
    eventosLoaded: false,
    heroSlides: [],
    heroSlidesLoaded: false,
    casosLoaded: false,
    equipo: [],
    equipoLoaded: false,
    siteLoaderVisible: true,
    valoresEquipo: valoresEquipo,
    fasesDesarrollo: fasesDesarrollo,
  },
  getters: {
    estudiosOrdenados(state) {
      return sortEstudiosByDateDesc(state.estudios)
    },
    casosOrdenados(state) {
      return sortCasosByFechaInicioDesc(state.casos)
    },
    prensaOrdenada(state) {
      return sortPrensaByDateDesc(state.prensa)
    },
    podcastsOrdenados(state) {
      return sortPodcastsByDateDesc(state.podcasts)
    },
    /** Los 3 estudios más recientes por fecha */
    highlightedEstudios(state) {
      return sortEstudiosByDateDesc(state.estudios).slice(0, 3)
    },
    /** Las 3 entradas de prensa más recientes por fecha */
    highlightedPrensa(state) {
      return sortPrensaByDateDesc(state.prensa).slice(0, 3)
    },
    /** Los 4 casos más recientes por fecha_inicio */
    highlightedCasos(state) {
      return sortCasosByFechaInicioDesc(state.casos).slice(0, 4)
    },
    casoByLink: (state) => (linkOrSlug) => findCasoBySlug(state.casos, linkOrSlug),
    homePageServices(state) {
      const categories = state.services ?? []
      const out = []
      for (const cat of categories) {
        const urlCategory = cat?.url_category ?? ''
        for (const svc of cat?.services ?? []) {
          if (svc?.is_in_home_page === true) {
            out.push({ ...svc, url_category: urlCategory })
          }
        }
      }
      return out
    },
    servicesByCategory: (state) => (categoryId) => {
      if (categoryId == null || String(categoryId).trim() === '') return []

      const id = String(categoryId).trim()
      const group = (state.services ?? []).find((c) => String(c?.id ?? '').trim() === id)

      return group?.services ?? []
    },
    prensaBySlug: (state) => (slug) => findPrensaBySlug(state.prensa, slug),
    /** Primeras N palabras de un texto (por defecto 15), con "…" si se recorta */
    prensaDescriptionExcerpt: () => (text, wordCount = 15) => {
      const raw = String(text ?? '').trim()
      if (!raw) return ''
      const words = raw.split(/\s+/).filter(Boolean)
      if (words.length <= wordCount) return raw
      return `${words.slice(0, wordCount).join(' ')}…`
    },
    equipoMiembros(state) {
      return state.equipo ?? []
    },
    getMiembroById: (state) => (id) =>
      (state.equipo ?? []).find((miembro) => miembro.id === id),
    /** Formatea fechas a dd-mm-aaaa (para uso en componentes si hace falta) */
    formatDate: () => formatDateToDdMmYyyy,
    /** Devuelve el año (aaaa) de una fecha */
    getYearFromDate: () => getYearFromDate,
  },
  mutations: {
    SET_HERO_SLIDES(state, slides) {
      state.heroSlides = slides;
      state.heroSlidesLoaded = true;
    },
    SET_ESTUDIOS(state, estudios) {
      state.estudios = normalizeApiCollectionDates(estudios, 'estudios');
      state.estudiosLoaded = true;
    },
    SET_CASOS(state, casos) {
      state.casos = normalizeApiCollectionDates(casos, 'casos');
      state.casosLoaded = true;
    },
    SET_EQUIPO(state, equipo) {
      state.equipo = equipo;
      state.equipoLoaded = true;
    },
    SET_PRENSA(state, prensa) {
      state.prensa = normalizeApiCollectionDates(prensa, 'prensa');
      state.prensaLoaded = true;
    },
    SET_EVENTOS(state, eventos) {
      state.eventos = normalizeApiCollectionDates(eventos, 'eventos');
      state.eventosLoaded = true;
    },
    SET_PODCASTS(state, podcasts) {
      state.podcasts = normalizeApiCollectionDates(podcasts, 'podcasts');
      state.podcastsLoaded = true;
    },
    SET_SITE_LOADER(state, visible) {
      state.siteLoaderVisible = visible;
    },
  },
  actions: {
    async loadHeroSlides({ commit, state }) {
      if (state.heroSlidesLoaded) return
      if (!heroSlidesLoadPromise) {
        heroSlidesLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('banners')
          commit('SET_HERO_SLIDES', raw)
        })().finally(() => {
          heroSlidesLoadPromise = null
        })
      }
      await heroSlidesLoadPromise
    },
    async loadEstudios({ commit, state }) {
      if (state.estudiosLoaded) return
      if (!estudiosLoadPromise) {
        estudiosLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('estudios')
          commit('SET_ESTUDIOS', raw)
        })().finally(() => {
          estudiosLoadPromise = null
        })
      }
      await estudiosLoadPromise
    },
    async loadCasos({ commit, state }) {
      if (state.casosLoaded) return
      if (!casosLoadPromise) {
        casosLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('casos')
          commit('SET_CASOS', raw)
        })().finally(() => {
          casosLoadPromise = null
        })
      }
      await casosLoadPromise
    },
    async loadEquipo({ commit, state }) {
      if (state.equipoLoaded) return
      if (!equipoLoadPromise) {
        equipoLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('equipo')
          commit('SET_EQUIPO', raw)
        })().finally(() => {
          equipoLoadPromise = null
        })
      }
      await equipoLoadPromise
    },
    async loadPrensa({ commit, state }) {
      if (state.prensaLoaded) return
      if (!prensaLoadPromise) {
        prensaLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('prensa')
          commit('SET_PRENSA', raw)
        })().finally(() => {
          prensaLoadPromise = null
        })
      }
      await prensaLoadPromise
    },
    async loadEventos({ commit, state }) {
      if (state.eventosLoaded) return
      if (!eventosLoadPromise) {
        eventosLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('eventos')
          commit('SET_EVENTOS', raw)
        })().finally(() => {
          eventosLoadPromise = null
        })
      }
      await eventosLoadPromise
    },
    async loadPodcasts({ commit, state }) {
      if (state.podcastsLoaded) return
      if (!podcastsLoadPromise) {
        podcastsLoadPromise = (async () => {
          const { fetchAll } = await import('@/services/contentApi.js')
          const raw = await fetchAll('podcasts')
          commit('SET_PODCASTS', raw)
        })().finally(() => {
          podcastsLoadPromise = null
        })
      }
      await podcastsLoadPromise
    },
  },
  modules: {
  }
})
